import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { monogram } from "../lib/format.js";

export function Field({ label, id, hint, children }) {
  return (
    <div className="afield">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="afield-hint">{hint}</p> : null}
    </div>
  );
}

export const emptyEvent = () => ({
  id: "", title: "", date: "", timeFrom: "", timeTo: "",
  location: "", host: "", description: "", link: "",
});

export const emptyMember = () => ({
  id: "", name: "", branch: "", city: "",
  description: "", website: "", contact: "", since: "", logo: "",
});

export function EventForm({ value, members, onSave, onCancel, busy }) {
  const [f, setF] = useState(value);
  useEffect(() => setF(value), [value]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.title.trim() && f.date;

  return (
    <div className="panel">
      <h3>{f.id ? "Anlass bearbeiten" : "Neuer Anlass"}</h3>

      <Field label="Titel" id="e-t">
        <input id="e-t" value={f.title} onChange={set("title")} placeholder="Stammtisch im ..." />
      </Field>

      <div className="row2">
        <Field label="Datum" id="e-d">
          <input id="e-d" type="date" value={f.date} onChange={set("date")} />
        </Field>
        <Field label="Ort" id="e-o">
          <input id="e-o" value={f.location} onChange={set("location")} placeholder="Strasse, Ort" />
        </Field>
      </div>

      <div className="row2">
        <Field label="Von" id="e-v">
          <input id="e-v" type="time" value={f.timeFrom} onChange={set("timeFrom")} />
        </Field>
        <Field label="Bis" id="e-b">
          <input id="e-b" type="time" value={f.timeTo} onChange={set("timeTo")} />
        </Field>
      </div>

      <Field label="Gastgeber" id="e-g">
        <select id="e-g" value={f.host} onChange={set("host")}>
          <option value="">Kein Gastgeber</option>
          {members.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
            </option>
          ))}
          {f.host && !members.some((m) => m.name === f.host) ? (
            <option value={f.host}>{f.host}</option>
          ) : null}
        </select>
      </Field>

      <Field label="Beschreibung" id="e-be">
        <textarea id="e-be" value={f.description} onChange={set("description")} />
      </Field>

      <Field
        label="Anmeldelink (optional)"
        id="e-l"
        hint="Ist ein Link gesetzt, führt die Schaltfläche auf der Seite dorthin statt zum Anmeldeformular."
      >
        <input id="e-l" value={f.link} onChange={set("link")} placeholder="doodle.com/..." />
      </Field>

      <div className="acts">
        <button className="b" onClick={() => onSave(f)} disabled={!ok || busy}>
          Anlass speichern
        </button>
        <button className="b b-ghost" onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
      </div>
      {!ok ? <p className="empty" style={{ marginTop: 10 }}>Titel und Datum sind nötig.</p> : null}
    </div>
  );
}

export function MemberForm({ value, onSave, onCancel, busy }) {
  const [f, setF] = useState(value);
  useEffect(() => setF(value), [value]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.name.trim();

  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState("");
  const fileRef = useRef(null);

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // damit dieselbe Datei erneut gewählt werden kann
    if (!file) return;

    setLogoError("");
    setLogoBusy(true);
    try {
      const { logo } = await api.uploadLogo(file);
      setF((prev) => ({ ...prev, logo }));
    } catch (ex) {
      setLogoError(ex.message);
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <div className="panel">
      <h3>{f.id ? "Mitglied bearbeiten" : "Neues Mitglied"}</h3>

      <Field label="Firma" id="m-n">
        <input id="m-n" value={f.name} onChange={set("name")} placeholder="Muster GmbH" />
      </Field>

      <div className="row2">
        <Field label="Branche" id="m-b" hint="Steuert den Filter auf der Seite.">
          <input id="m-b" value={f.branch} onChange={set("branch")} placeholder="Handwerk" />
        </Field>
        <Field label="Ort" id="m-o">
          <input id="m-o" value={f.city} onChange={set("city")} placeholder="Jona" />
        </Field>
      </div>

      <Field label="Kurzbeschrieb" id="m-d">
        <textarea id="m-d" value={f.description} onChange={set("description")} />
      </Field>

      <div className="row2">
        <Field label="Ansprechperson" id="m-c">
          <input id="m-c" value={f.contact} onChange={set("contact")} />
        </Field>
        <Field label="Dabei seit" id="m-s">
          <input id="m-s" value={f.since} onChange={set("since")} placeholder="2025" />
        </Field>
      </div>

      <Field label="Website" id="m-w">
        <input id="m-w" value={f.website} onChange={set("website")} placeholder="firma.ch" />
      </Field>

      <div className="afield">
        <label htmlFor="m-l">Firmenlogo</label>
        <div className="logo-pick">
          <div className="logo-preview" data-empty={!f.logo} aria-hidden="true">
            {f.logo ? (
              <img src={`/uploads/${f.logo}`} alt="" />
            ) : (
              <span>{monogram(f.name)}</span>
            )}
          </div>

          <div className="logo-pick-side">
            <div className="acts" style={{ marginTop: 0 }}>
              <button
                className="b b-ghost"
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={logoBusy || busy}
              >
                {logoBusy ? "Wird geladen ..." : f.logo ? "Logo ersetzen" : "Logo wählen"}
              </button>
              {f.logo ? (
                <button
                  className="b b-del"
                  type="button"
                  disabled={logoBusy || busy}
                  onClick={() => setF({ ...f, logo: "" })}
                >
                  Entfernen
                </button>
              ) : null}
            </div>
            <p className="afield-hint">
              PNG, JPEG, WebP oder SVG, höchstens 512 KB. Ohne Logo zeigt die Seite die
              Anfangsbuchstaben.
            </p>
          </div>

          <input
            id="m-l"
            ref={fileRef}
            type="file"
            className="visually-hidden"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={pickLogo}
          />
        </div>
        {logoError ? (
          <p className="form-error" role="alert">
            {logoError}
          </p>
        ) : null}
      </div>

      <div className="acts">
        <button className="b" onClick={() => onSave(f)} disabled={!ok || busy}>
          Mitglied speichern
        </button>
        <button className="b b-ghost" onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
      </div>
      {!ok ? <p className="empty" style={{ marginTop: 10 }}>Der Firmenname ist nötig.</p> : null}
    </div>
  );
}
